import { IKey } from '@/common/constants/ikey'
import {
  canonicalTreeNodeId,
  TREE_TABLE_UNIQUE_EMPTY_SLOT,
} from '@/common/utils/treeTableIds'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'

export const TREE_TABLE_KEY_JOINER = '|'

export type TreeTableColumnMeta = {
  name: string
  order?: number
  uniqueValues?: boolean
  uniqueNodeValues?: boolean
}

function toPlainVal(val: unknown): unknown {
  if (val != null && typeof val === 'object' && (val as { __v_isRef?: boolean }).__v_isRef === true) {
    return toPlainVal((val as { value: unknown }).value)
  }
  if (
    val != null &&
    typeof val === 'object' &&
    !('value' in val) &&
    ('_value' in val || '_rawValue' in val)
  ) {
    const innerValue = (val as { _value?: unknown; _rawValue?: unknown })._value ??
      (val as { _rawValue?: unknown })._rawValue
    if (innerValue === null || ['string', 'number', 'boolean'].includes(typeof innerValue)) {
      return innerValue
    }
  }
  if (val != null && typeof val === 'object' && 'value' in val && Object.keys(val as object).length <= 2) {
    return (val as { value: unknown }).value
  }
  return val
}

function extractCellValue(cell: unknown): unknown {
  if (cell == null || typeof cell !== 'object') return cell
  if ('value' in cell) return toPlainVal((cell as { value: unknown }).value)
  return cell
}

/** Извлечь значение поля из строки UniDataTable (fields, плоский ключ, backing _originalRow). */
export function getFieldValueFromRow(row: unknown, fieldName: string): unknown {
  if (!row || typeof row !== 'object') return undefined
  const r = row as Record<string, unknown>
  const fromFields = (r.fields as Record<string, unknown> | undefined)?.[fieldName]
  if (fromFields !== undefined) {
    return extractCellValue(fromFields)
  }
  if (r[fieldName] !== undefined) {
    return extractCellValue(r[fieldName])
  }
  const backing = r._originalRow ?? r._rowData
  if (backing && backing !== row && typeof backing === 'object') {
    return getFieldValueFromRow(backing, fieldName)
  }
  return undefined
}

/**
 * Служебные флаги строки TreeTable из UniData (procedure / onLoad `setField`), не из XML элемента.
 * Как Swing convertUniRowToNode: поле действует, если задано на строке.
 */
export function applyTreeTablePlatformRowFlags(
  row: unknown,
  result: Record<string, unknown>,
): void {
  const allow =
    getFieldValueFromRow(row, IKey.TT_IS_ALLOW_CHILDREN) ?? result[IKey.TT_IS_ALLOW_CHILDREN] ?? false
  result[IKey.TT_IS_ALLOW_CHILDREN] = allow
  result.isAllowChildren = allow

  const expand =
    getFieldValueFromRow(row, IKey.EXPAND_BY_DEFAULT) ?? result[IKey.EXPAND_BY_DEFAULT] ?? false
  result[IKey.EXPAND_BY_DEFAULT] = expand
  result.expandByDefault = expand

  const hide = getFieldValueFromRow(row, IKey.TT_HIDE_CHECKBOX) ?? result[IKey.TT_HIDE_CHECKBOX]
  result[IKey.TT_HIDE_CHECKBOX] = hide ?? false
  result.hideCheckbox = result[IKey.TT_HIDE_CHECKBOX]

  const forbid =
    getFieldValueFromRow(row, IKey.TT_FORBID_TO_SELECT_NODE) ?? result[IKey.TT_FORBID_TO_SELECT_NODE]
  if (forbid !== undefined) {
    result[IKey.TT_FORBID_TO_SELECT_NODE] = forbid
  }

  const drop = getFieldValueFromRow(row, IKey.TT_IS_DROP_TARGET) ?? result[IKey.TT_IS_DROP_TARGET]
  if (drop !== undefined) {
    result[IKey.TT_IS_DROP_TARGET] = drop
  }

  const count = getFieldValueFromRow(row, IKey.TT_COUNT_CHILDREN) ?? result[IKey.TT_COUNT_CHILDREN]
  if (count !== undefined) {
    result[IKey.TT_COUNT_CHILDREN] = count
  }
}

/** Плоский объект строки для ключей и сравнения (см. ElementRenderer.flattenRowData). */
export function flattenTreeTableRowData(
  row: unknown,
  columns: TreeTableColumnMeta[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  const r = row as { fields?: Record<string, unknown> } | null
  if (r?.fields) {
    for (const fieldName of Object.keys(r.fields)) {
      result[fieldName] = extractCellValue(r.fields[fieldName])
    }
  }
  for (const col of columns) {
    if (!col.name || result.hasOwnProperty(col.name)) continue
    const value = getFieldValueFromRow(row, col.name)
    if (value !== undefined) result[col.name] = value
  }
  applyTreeTablePlatformRowFlags(row, result)
  const itemsField = getFieldValueFromRow(row, IKey.ITEMS)
  if (itemsField) {
    const itemsRows = extractUniDataTableRows(itemsField)
    if (itemsRows.length > 0) result._items = itemsRows
  }
  return result
}

export function buildTreeTableRowKey(
  row: unknown,
  columns: TreeTableColumnMeta[],
  isAllowChildren: unknown,
  parentKey: string,
  level: number,
  rowIndex: number,
): string {
  const sorted = [...columns].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const nodeCols = sorted.filter((c) => c.uniqueNodeValues === true).map((c) => c.name).filter(Boolean)
  const leafCols = sorted.filter((c) => c.uniqueValues === true).map((c) => c.name).filter(Boolean)
  const useNode = isAllowChildren === true || isAllowChildren === 'true'
  const names = useNode ? nodeCols : leafCols
  if (names.length > 0) {
    const parts = names.map((n) => {
      const v = canonicalTreeNodeId(getFieldValueFromRow(row, n))
      return v === '' ? TREE_TABLE_UNIQUE_EMPTY_SLOT : v
    })
    return parts.join(TREE_TABLE_KEY_JOINER)
  }
  const base = parentKey !== '' ? parentKey : 'tt-root'
  return `${base}${TREE_TABLE_KEY_JOINER}L${level}${TREE_TABLE_KEY_JOINER}i${rowIndex}`
}

export function rowsMatchTreeTableIdentity(
  rowA: unknown,
  rowB: unknown,
  columns: TreeTableColumnMeta[],
): boolean {
  const flatA = flattenTreeTableRowData(rowA, columns)
  const flatB = flattenTreeTableRowData(rowB, columns)
  const sorted = [...columns].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const nodeCols = sorted.filter((c) => c.uniqueNodeValues === true).map((c) => c.name).filter(Boolean)
  const leafCols = sorted.filter((c) => c.uniqueValues === true).map((c) => c.name).filter(Boolean)
  const useNode = flatB.isAllowChildren === true || flatB.isAllowChildren === 'true'
  const names = (useNode ? nodeCols : leafCols).length > 0 ? (useNode ? nodeCols : leafCols) : ['_id']
  for (const name of names) {
    const va = canonicalTreeNodeId(flatA[name])
    const vb = canonicalTreeNodeId(flatB[name])
    if (va === '' && vb === '') continue
    if (va !== vb) return false
  }
  return names.length > 0
}

function getTreeItemOriginalRow(item: unknown): unknown {
  if (!item || typeof item !== 'object') return item
  const rec = item as Record<string, unknown>
  const data =
    rec.data && typeof rec.data === 'object' ? (rec.data as Record<string, unknown>) : rec
  return data._originalRow ?? data
}

/**
 * Ключи узлов по цепочке pathRows, сопоставляя строки с уже собранным деревом items (как Swing markFinedNode).
 */
export function resolvePathKeysFromTreeItems(
  pathRows: unknown[],
  rootItems: unknown[],
  columns: TreeTableColumnMeta[],
): string[] {
  const keys: string[] = []
  let siblings = Array.isArray(rootItems) ? rootItems : []
  for (const pathRow of pathRows) {
    let found: Record<string, unknown> | null = null
    for (const item of siblings) {
      const orig = getTreeItemOriginalRow(item)
      if (rowsMatchTreeTableIdentity(pathRow, orig, columns)) {
        found = item as Record<string, unknown>
        break
      }
    }
    if (!found) break
    const k = String(found.key ?? '').trim()
    if (!k) break
    keys.push(k)
    const children = found.children
    siblings = Array.isArray(children) ? children : []
  }
  return keys
}

export function pathRowsToNodeKeys(
  pathRows: unknown[],
  columns: TreeTableColumnMeta[],
  siblingRowIndexForLevel?: (level: number) => number,
): string[] {
  const keys: string[] = []
  let parentKey = ''
  for (let level = 0; level < pathRows.length; level++) {
    const row = pathRows[level]
    const flat = flattenTreeTableRowData(row, columns)
    const rowIndex = siblingRowIndexForLevel?.(level) ?? level
    const key = buildTreeTableRowKey(row, columns, flat.isAllowChildren, parentKey, level, rowIndex)
    keys.push(key)
    parentKey = key
  }
  return keys
}

function cloneUniDataRow(row: unknown): Record<string, unknown> {
  try {
    return JSON.parse(JSON.stringify(row)) as Record<string, unknown>
  } catch {
    return { ...(row as Record<string, unknown>) }
  }
}

function getNestedItemRows(row: Record<string, unknown>): any[] {
  const itemsField = (row.fields as Record<string, unknown> | undefined)?.items ?? row.items
  if (!itemsField) return []
  return extractUniDataTableRows(itemsField)
}

function setNestedItemRows(row: Record<string, unknown>, childRows: any[]): void {
  if (!row.fields || typeof row.fields !== 'object') {
    row.fields = {}
  }
  const fields = row.fields as Record<string, unknown>
  fields.items = {
    type: 'UniDataTable',
    value: {
      rows: childRows,
      columns: [],
      attributes: {},
      empty: childRows.length === 0,
    },
  }
}

function ensureChildRowOnHost(
  host: Record<string, unknown>,
  childRow: unknown,
  columns: TreeTableColumnMeta[],
): any[] {
  let children = getNestedItemRows(host)
  const exists = children.some((r) => rowsMatchTreeTableIdentity(r, childRow, columns))
  if (!exists) {
    if (children.length === 0) {
      setNestedItemRows(host, [cloneUniDataRow(childRow)])
    } else {
      children.push(cloneUniDataRow(childRow))
    }
    children = getNestedItemRows(host)
  }
  return children
}

/**
 * Вставить цепочку pathRows в дерево backing (корневые rows), если узлов ещё нет.
 */
export function mergePathRowsIntoBackingTree(
  backingRows: any[],
  pathRows: unknown[],
  columns: TreeTableColumnMeta[],
): any[] {
  if (!Array.isArray(pathRows) || pathRows.length === 0) {
    return backingRows
  }
  const root = backingRows.map((r) => cloneUniDataRow(r))
  let currentLevel = root

  for (let i = 0; i < pathRows.length; i++) {
    const pathRow = pathRows[i]
    let idx = currentLevel.findIndex((r) => rowsMatchTreeTableIdentity(r, pathRow, columns))
    if (idx < 0) {
      currentLevel.push(cloneUniDataRow(pathRow))
      idx = currentLevel.length - 1
    }
    const host = currentLevel[idx] as Record<string, unknown>
    if (i < pathRows.length - 1) {
      currentLevel = ensureChildRowOnHost(host, pathRows[i + 1], columns)
    }
  }
  return root
}
